/*
    ActionScheduler is a scheduler that handle background-processes. It
    makes it possible to trigger an action after X-seconds, and maintains those
    actions.

    Action {
        name: String, // Random name for the action. Can therefore subscribe to an action by a given name
        action: Function,
        duration: [Number, Array<Number>]
        durationHandler: Function // Requires duration as a Number, where it works as the start timer
    }

    Example:
    action = {
        name: "MyAction",
        action: () => console.log("Hello world!"),
        duration: 30000, // Starts for the first time after 30 seconds
        durationHandler: (i, prev) => i === 30 ? null : i*prev, // Doubles the timer, and stops after 30 attempts
    }
*/

class ActionScheduler {
    static actions = {};

    static schedule(action) {
        // Validate input
        if (!action || !action.action || !action.duration || !action.name) {
            return false;
        }

        const isExistingAction = Boolean(this.actions[action.name]);

        // Increase index is this is a reschedule
        if (isExistingAction && this.actions[action.name].timeLeft === undefined) {
            this.actions[action.name].index = (this.actions[action.name].index || 0) + 1;
        }

        let duration = action.duration;
        if (isExistingAction && this.actions[action.name].timeLeft !== undefined) {
            // If action was stopped before it was finished, used the time it had left
            duration = this.actions[action.name].timeLeft;
        } else if (action.duration instanceof Array) {
            // Check if there is a duration to set, if not, do not register
            if (action.duration.length === 0) {
                return false;
            }

            // Get the next duration from the array
            duration = action.duration.shift();
            if (!(duration instanceof Number)) {
                return false;
            }
        } else if (action.durationHandler instanceof Function) {
            // If there is a function that handles the next timer, use it
            let prevDuration = duration;
            let index = 0;
            if (this.actions[action.name]) {
                prevDuration = this.actions[action.name].duration || 0;
                index = this.actions[action.name].index || 0;
            }
            if (index > 0) {
                duration = action.durationHandler(
                    index,
                    prevDuration,
                    this.actions[action.name].actionObject
                );
            }

            if (!(typeof duration === 'number')) {
                return false;
            }
        }

        const newAction = {
            actionObject: action,
            timeOut: timeOutId,
            duration: duration,
            index: this.actions[action.name] ? this.actions[action.name].index : 0,
            startedAt: Date.now(),
        };

        // Clear existing timeout if it exists
        if (this.actions[action.name]) {
            clearTimeout(this.actions[action.name].timeOut);
        }

        // Save action in actions
        this.actions[action.name] = newAction;

        // Schedule action
        const timeOutId = setTimeout(() => {
            const index = this.actions[action.name].index || 0;
            action.action(index);
        }, duration);
        this.actions[action.name].timeOut = timeOutId;
        return true;
    }

    static cancel(name) {
        const action = this.actions[name];
        if (!action) {
            return false; // Nothing was deleted
        }

        if (action.timeOut) {
            clearTimeout(action.timeOut);
        }

        delete this.actions[name];
        return true;
    }

    static stop(name) {
        const action = this.actions[name];
        if (!action) {
            return false;
        }

        // Persist time left
        const timeUsed = Date.now() - (action.startedAt || 0);
        action.timeLeft = action.duration - timeUsed > 0 ? action.duration - timeUsed : undefined;

        if (typeof action.timeOut === 'number') {
            clearTimeout(action.timeOut);
            delete action.timeOut;
        }
        // action.index -= 1; // Reduce index. Will be increased when rescheduled
    }

    static start(name) {
        const action = this.actions[name];
        if (!action || action.timeOut) {
            return false;
        }

        this.schedule(action.actionObject);
    }

    static isDone(name) {
        const action = this.actions[name];
        if (!action) {
            return true;
        }

        return !Boolean(action.timeOut);
    }

    static hasAction(name) {
        return Boolean(this.actions[name]);
    }
}

export default ActionScheduler;
